import { useEffect, useMemo, useState } from "react";

import { gql, useLazyQuery } from "@apollo/client";

interface HeaderLink {
  label: string;
  href: string;
}
interface Crumb {
  label: string;
  href?: string;
}

export interface SignatureProfileHeaderData {
  logoSrc: string;
  brandName: string;
  nav: HeaderLink[];
  breadcrumbs: Crumb[];
}

const SEARCH_SIGNATURE_PROFILES = gql`
  query SearchSignatureProfiles(
    $query: String
    $artistFirstName: String
    $artistLastName: String
    $styleTags: String
    $limit: Int
  ) {
    signatureProfiles(
      query: $query
      artistFirstName: $artistFirstName
      artistLastName: $artistLastName
      styleTags: $styleTags
      limit: $limit
      offset: 0
    ) {
      id
      artistFirstName
      artistLastName
      artistPictureLargeUrl
      artistPictureSmallUrl
      publicLink
      styleTags
    }
  }
`;

export const SignatureProfileHeader: React.FC<{
  data: SignatureProfileHeaderData;
}> = ({ data }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const [runSearch, { data: results }] = useLazyQuery(
    SEARCH_SIGNATURE_PROFILES,
    {
      fetchPolicy: "cache-and-network",
    }
  );

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 0);

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const handle = setTimeout(() => {
      const term = searchTerm.trim();

      if (term.length === 0) return;
      runSearch({
        variables: {
          // broaden search by applying term to all three supported filters
          artistFirstName: term,
          artistLastName: term,
          styleTags: term,
          limit: 5,
        },
      });
      setOpen(true);
    }, 250);

    return () => clearTimeout(handle);
  }, [runSearch, searchTerm]);

  const profiles = useMemo(() => results?.signatureProfiles ?? [], [results]);

  return (
    <header
      className={`${
        scrolled ? "bg-white/70 backdrop-blur" : "bg-transparent"
      } fixed top-0 w-full z-50 transition-all duration-300 py-4`}
    >
      <div className="container mx-auto px-4 flex justify-between items-center">
        <div className="flex items-center gap-8">
          <a className="flex items-center gap-2" href="/">
            <img alt="Artify" className="h-8 w-auto" src={data.logoSrc} />
            <span className="font-playfair text-xl font-light">
              {data.brandName}
            </span>
          </a>
          <nav className="hidden md:block">
            <ul className="flex space-x-6">
              {data.nav.map((item) => (
                <li key={item.label}>
                  <a
                    href={item.href}
                    className="hover:text-artist-accent transition-colors"
                  >
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        </div>
        <div className="hidden md:flex items-center gap-4">
          <div className="relative">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onFocus={() => setOpen(true)}
              onBlur={() => setTimeout(() => setOpen(false), 150)}
              placeholder="Search artists, artworks..."
              className="pl-10 pr-4 py-2 rounded-full border border-gray-300 focus:outline-none focus:ring-2 focus:ring-artist-accent focus:border-transparent"
            />
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="lucide lucide-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.3-4.3" />
            </svg>

            {open && profiles.length > 0 && (
              <div className="absolute mt-2 w-80 bg-white rounded-md shadow-lg border z-50">
                <ul className="max-h-80 overflow-auto">
                  {profiles.map((p: any) => (
                    <li key={p.id} className="px-3 py-2 hover:bg-gray-50">
                      <a
                        href={p.publicLink || `/signature_profiles/${p.id}`}
                        className="flex items-center gap-3"
                      >
                        {p.artistPictureLargeUrl ? (
                          <img
                            src={p.artistPictureLargeUrl}
                            alt={`${p.artistFirstName || ""} ${
                              p.artistLastName || ""
                            }`.trim()}
                            className="w-8 h-8 rounded object-cover"
                          />
                        ) : (
                          <div className="w-8 h-8 rounded bg-gray-200" />
                        )}
                        <div>
                          <div className="text-sm font-medium">
                            {[p.artistFirstName, p.artistLastName]
                              .filter(Boolean)
                              .join(" ") || "Unnamed"}
                          </div>
                          {p.styleTags?.length ? (
                            <div className="text-xs text-gray-500">
                              {p.styleTags.join(", ")}
                            </div>
                          ) : null}
                        </div>
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <button
            type="button"
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-9 px-3 rounded-full"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="lucide lucide-user mr-2"
            >
              <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
            Account
          </button>
        </div>
        <div className="md:hidden">
          <button
            type="button"
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 hover:bg-accent hover:text-accent-foreground h-10 w-10"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="lucide lucide-menu"
            >
              <line x1="4" x2="20" y1="12" y2="12" />
              <line x1="4" x2="20" y1="6" y2="6" />
              <line x1="4" x2="20" y1="18" y2="18" />
            </svg>
          </button>
        </div>
      </div>
      {!scrolled && (
        <div className="container mx-auto px-4 pt-2 opacity-100 transition-opacity duration-300">
          <div className="text-sm text-muted-foreground">
            <ul className="flex flex-wrap">
              {data.breadcrumbs.map((c) => (
                <li key={c.label} className="breadcrumb-item">
                  {c.href ? (
                    <a href={c.href} className="hover:text-artist-accent">
                      {c.label}
                    </a>
                  ) : (
                    <span>{c.label}</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </header>
  );
};
